/**
 * This browser's per-group read watermarks.
 *
 * Only a floor: the shared signal is the per-participant receipt on each message, and the watermark exists
 * for the messages that predate receipts, which would otherwise all resurface as unread. It is kept per
 * browser, so it is this module's job to forget it when the pairing it belongs to goes away.
 */
const READS_KEY = "failover-group-reads";

export function loadGroupReads(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(READS_KEY) || "{}") as Record<string, number>;
  } catch {
    return {};
  }
}

export function saveGroupReads(reads: Record<string, number>): void {
  try {
    localStorage.setItem(READS_KEY, JSON.stringify(reads));
  } catch {
    /* storage full or blocked — the watermark is a floor, not the read state itself */
  }
}

/**
 * Is this key inside `scope` — one group (`pairing/group`) or a whole pairing?
 *
 * The separator is required rather than a bare prefix match, so one id can't take another that merely begins
 * the same way.
 */
export function keyIsUnder(key: string, scope: string): boolean {
  return key === scope || key.startsWith(`${scope}/`);
}

/**
 * Forget every watermark belonging to a pairing — what unpairing has to do.
 *
 * Removing the pairing alone leaves these behind, and pairing the same device again brings them back: its
 * groups open already marked read, hiding whatever arrived in the meantime.
 */
export function forgetPairingReads(pairingId: string): void {
  const reads = loadGroupReads();
  const kept = Object.fromEntries(Object.entries(reads).filter(([k]) => !keyIsUnder(k, pairingId)));
  saveGroupReads(kept);
}
